import React, { useEffect, useState } from 'react';
import { Card, Col, Row, Spin, Typography, Button, Space, Divider, Tag, message, Modal } from 'antd';
import { AppstoreOutlined, DownloadOutlined, ExclamationCircleOutlined, MessageOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  getDashboardStats,
  getProductVersionsSummary,
  getVersionDetail,
  downloadVersionFile,
  downloadDailyVersionFile,
  downloadGitLabVersionFile,
} from '../services/api';
import {
  PRODUCT_COLORS, PRODUCT_ORDER, DAILY_PRODUCTS, GITLAB_PRODUCTS,
  getProductDisplayName, isMainVersionProductVisible,
} from '../utils/format';

const { Text, Title } = Typography;

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productDetails, setProductDetails] = useState({});

  useEffect(() => {
    getDashboardStats()
      .then((res) => {
        const data = res.data.data;
        setStats(data);
        setLoading(false);
        if (data.productStats?.length) {
          data.productStats.forEach(async (p) => {
            try {
              const resSummary = await getProductVersionsSummary(p.id);
              setProductDetails((prev) => ({ ...prev, [p.id]: resSummary.data.data }));
            } catch (e) {
              setProductDetails((prev) => ({ ...prev, [p.id]: null }));
            }
          });
        }
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const sortedProducts = [...stats.productStats].filter(isMainVersionProductVisible).sort((a, b) => {
    const idxA = PRODUCT_ORDER.indexOf(a.model);
    const idxB = PRODUCT_ORDER.indexOf(b.model);
    return idxA - idxB;
  });

  const showVersionRemarksBeforeDownload = async (version, productModel, downloadTask) => {
    const runDownloadTask = async () => {
      try {
        await downloadTask();
      } catch (err) {
        message.error(err.response?.data?.message || '下载失败');
      }
    };

    if (!version || Number(version.remark_count || 0) <= 0) {
      await runDownloadTask();
      return;
    }

    let remarks = [];
    try {
      const res = await getVersionDetail(version.id);
      remarks = (res.data?.data?.remarks || [])
        .filter((remark) => String(remark.content || '').trim());
    } catch (err) {
      message.error(err.response?.data?.message || '获取版本备注失败');
      return;
    }

    if (remarks.length === 0) {
      await runDownloadTask();
      return;
    }

    Modal.confirm({
      title: `版本备注 - ${productModel} v${version.version}`,
      icon: <ExclamationCircleOutlined />,
      width: 640,
      okText: '继续下载',
      cancelText: '取消',
      content: (
        <div className="download-remark-modal">
          <Text type="secondary">此版本有 {remarks.length} 条备注，请确认后继续下载。</Text>
          <div className="download-remark-list">
            {remarks.map((remark, index) => (
              <div className="download-remark-item" key={remark.id || `${remark.created_at}-${index}`}>
                <div className="download-remark-meta">
                  <Text strong>{remark.created_by_name || remark.created_by_email || '未知用户'}</Text>
                  <Text type="secondary">{dayjs(remark.created_at).format('YYYY-MM-DD HH:mm')}</Text>
                </div>
                <div className="download-remark-content">{remark.content}</div>
              </div>
            ))}
          </div>
        </div>
      ),
      onOk: runDownloadTask,
    });
  };

  const handleDownload = async (event, downloadTask, options = {}) => {
    event.stopPropagation();
    if (options.version) {
      await showVersionRemarksBeforeDownload(options.version, options.productModel, downloadTask);
      return;
    }

    try {
      await downloadTask();
    } catch (err) {
      message.error(err.response?.data?.message || '下载失败');
    }
  };

  return (
    <div className="page-shell">
      <div className="page-heading">
        <div>
          <div className="page-kicker">Welzek Release Center</div>
          <Title level={3} className="page-heading-title">版本仪表盘</Title>
          <Text className="page-heading-subtitle">聚合各产品最新发布版本与每日构建，支持一键下载和快速进入版本列表。</Text>
        </div>
        <Tag color="#014fa2" style={{ margin: 0, padding: '5px 12px' }}>
          {sortedProducts.length} 个产品线
        </Tag>
      </div>

      <Row gutter={[16, 16]}>
        {sortedProducts.map((p) => {
          const color = PRODUCT_COLORS[p.model] || '#1677ff';
          const detail = productDetails[p.id];
          const hasDaily = DAILY_PRODUCTS.includes(p.model);
          return (
            <Col xs={24} md={12} lg={12} key={p.id}>
              <Card
                hoverable
                className="product-card"
                style={{ height: '100%', cursor: 'pointer' }}
                bodyStyle={{ padding: 18 }}
                onClick={() => navigate(`/versions/release?product_id=${p.id}`)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, position: 'relative', zIndex: 1 }}>
                  <div
                    className="product-icon"
                    style={{
                      color,
                      background: `${color}12`,
                    }}
                  >
                    <AppstoreOutlined style={{ color, fontSize: 24 }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text strong style={{ fontSize: 20, display: 'block', color: '#172033' }}>
                      {getProductDisplayName(p.model)}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      共 {p.version_count} 个版本
                    </Text>
                  </div>
                  <Tag color={color} style={{ fontSize: 14, padding: '4px 10px' }}>
                    {p.version_count}
                  </Tag>
                </div>

                <Divider style={{ margin: '18px 0', borderColor: '#e8eef6' }} />

                <div className="version-row">
                  <Space align="center" size={10}>
                    <span className="status-strip" style={{ background: '#1f9d55' }} />
                    <div>
                      <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>最新发布版本</Text>
                      {detail?.latestRelease ? (
                        <div className="dashboard-release-main">
                          <Text strong style={{ fontSize: 18, fontFamily: 'monospace' }}>
                            v{detail.latestRelease.version}
                          </Text>
                          {Number(detail.latestRelease.remark_count) > 0 && (
                            <span className="dashboard-release-remark-count">
                              <MessageOutlined />
                              备注 {detail.latestRelease.remark_count} 条
                            </span>
                          )}
                        </div>
                      ) : (
                        <Text type="secondary">暂无</Text>
                      )}
                    </div>
                  </Space>
                  {detail?.latestRelease ? (
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      onClick={(e) => handleDownload(e, () => (
                        GITLAB_PRODUCTS.includes(p.model) && !detail.latestRelease.file_path
                          ? downloadGitLabVersionFile(detail.latestRelease.id)
                          : downloadVersionFile(detail.latestRelease.id)
                      ), {
                        version: detail.latestRelease,
                        productModel: getProductDisplayName(p.model),
                      })}
                    >
                      下载
                    </Button>
                  ) : (
                    <Button disabled>下载</Button>
                  )}
                </div>

                {hasDaily && (
                  <div className="version-row">
                    <Space align="center" size={10}>
                      <span className="status-strip" style={{ background: '#ff7f0d' }} />
                      <div>
                        <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>最新每日版本</Text>
                        {detail?.latestDaily ? (
                          <div className="dashboard-release-main">
                            <Text strong style={{ fontSize: 18, fontFamily: 'monospace' }}>
                              v{detail.latestDaily.version}
                            </Text>
                            {Number(detail.latestDaily.remark_count) > 0 && (
                              <span className="dashboard-release-remark-count">
                                <MessageOutlined />
                                备注 {detail.latestDaily.remark_count} 条
                              </span>
                            )}
                          </div>
                        ) : (
                          <Text type="secondary">暂无</Text>
                        )}
                      </div>
                    </Space>
                    {detail?.latestDaily ? (
                      <Button
                        icon={<DownloadOutlined />}
                        onClick={(e) => handleDownload(e, () => downloadDailyVersionFile(p.id), {
                          version: detail.latestDaily,
                          productModel: getProductDisplayName(p.model),
                        })}
                      >
                        下载
                      </Button>
                    ) : (
                      <Button disabled>下载</Button>
                    )}
                  </div>
                )}
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}
